import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../components/Icon.jsx';
import { Sheet } from '../components/Sheet.jsx';
import { useArtikelKatalog, useMe } from '../hooks/data.js';
import { alleBilderLaden, bilderLadenAnhalten, useBilderLaden } from '../state/bilderLaden.js';
import { num } from '../lib/format.js';
import { useAuth } from '../state/auth.jsx';
import { useTimer } from '../state/timer.jsx';
import { initials } from '../lib/format.js';
import { useOutbox } from '../state/outbox.jsx';
import { vorgemerkteEntwuerfe } from '../lib/entwuerfe.js';

/** Angemeldeter Benutzer und Abmelden. Mit laufender Zeit wird nicht abgemeldet – sonst ginge sie verloren. */
export function KontoSheet({ onClose }) {
  const me = useMe();
  const { logout } = useAuth();
  const timer = useTimer();
  const pending = useOutbox().filter((i) => i.uid === me.data?.uid).length;
  const formulare = vorgemerkteEntwuerfe().length;
  const blocked = timer.running || pending > 0 || formulare > 0;
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const firmenadmin = me.data?.rollen?.includes('firmenadmin');
  const doLogout = async () => {
    setError('');
    setBusy(true);
    try {
      await logout();
      onClose();
    } catch (err) {
      setError(`Abmelden fehlgeschlagen (${err.message}). Bitte bei Netz erneut versuchen – bis dahin bist du noch angemeldet.`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet title="Angemeldet" onClose={onClose}>
      <div className="row">
        <span className="avatar lg">{initials(me.data?.name)}</span>
        <span className="stack" style={{ gap: 2 }}>
          <span style={{ fontSize: 17, fontWeight: 800 }}>{me.data?.name ?? '…'}</span>
          <span className="sub">{me.data?.email}</span>
        </span>
      </div>
      {timer.running && <div className="notice" role="status">Es läuft noch Zeit auf {timer.nummer}. Bitte zuerst stoppen und buchen, dann abmelden.</div>}
      {pending > 0 && <div className="notice" role="status">{pending} {pending === 1 ? 'Buchung ist' : 'Buchungen sind'} noch nicht gesendet. Bitte warten, bis wieder Netz da ist – sonst gehen sie verloren.</div>}
      {formulare > 0 && <div className="notice" role="status">{formulare} {formulare === 1 ? 'Formular wartet' : 'Formulare warten'} auf Netz (z. B. Messprotokoll). Bitte warten, bis wieder Netz da ist – sonst gehen sie verloren.</div>}
      {error && <div className="notice err" role="alert">{error}</div>}
      <button type="button" className="btn secondary lg block" onClick={() => { onClose(); navigate('/apps'); }}>
        <Icon name="extra" size={20} stroke={2.2} />Apps und Module
      </button>
      {firmenadmin && (
        <button type="button" className="btn secondary lg block" onClick={() => { onClose(); navigate('/verwaltung'); }}>
          <Icon name="edit" size={20} stroke={2.2} />Verwaltung der Firma
        </button>
      )}
      <BilderLaden />
      <button className="btn secondary lg block" disabled={blocked || busy} onClick={doLogout}>{busy ? 'Abmelden …' : 'Abmelden'}</button>
      <span className="sub" style={{ fontSize: 12, textAlign: 'center' }}>Auf geteilten Geräten nach der Arbeit immer abmelden.</span>
    </Sheet>
  );
}

/** Alle Artikelbilder einmal laden: Bildspeicher (für alle) und dieses Gerät füllen. */
function BilderLaden() {
  const katalog = useArtikelKatalog();
  const z = useBilderLaden();
  const anzahl = katalog.data?.artikel?.length ?? 0;
  const prozent = z.gesamt ? Math.round((z.fertig / z.gesamt) * 100) : 0;
  return (
    <div className="stack" style={{ gap: 8, padding: 12, borderRadius: 14, background: 'var(--bg)' }}>
      <span style={{ fontSize: 15, fontWeight: 800 }}>Artikelbilder</span>
      {!z.laeuft && (
        <span className="sub">Bilder werden einmal aus Exxas geholt und dann für alle Benutzer gespeichert – Exxas wird danach nicht mehr gefragt. «Fehlende laden» holt nur, was noch fehlt; «Alle neu aus Exxas» prüft jedes Bild (nach Änderungen in Exxas). Dauert 20–30 Minuten, Buchungen haben Vorrang. App offen lassen (am besten am PC).</span>
      )}
      {(z.laeuft || z.ende) && (
        <>
          <div className="bar" aria-hidden="true"><div style={{ width: `${prozent}%` }} /></div>
          <span className="sub tn" role="status">
            {num(z.fertig)} von {num(z.gesamt)} · {num(z.mitBild)} mit Bild · {num(z.ohneBild)} ohne{z.fehler ? ` · ${num(z.fehler)} Fehler` : ''}
            {z.wartet && ' · wartet kurz (Exxas-Kontingent)'}
            {z.ende === 'fertig' && ' · fertig'}
            {z.ende === 'angehalten' && ' · angehalten'}
            {z.ende === 'abgemeldet' && ' · abgebrochen (bitte neu anmelden)'}
          </span>
        </>
      )}
      {z.laeuft
        ? <button className="btn secondary block" onClick={bilderLadenAnhalten}>Anhalten</button>
        : (
          <div className="row">
            <button className="btn secondary" style={{ flex: 1 }} disabled={!anzahl} onClick={() => alleBilderLaden(katalog.data.artikel)}>Fehlende laden</button>
            <button className="btn secondary" style={{ flex: 1 }} disabled={!anzahl} onClick={() => alleBilderLaden(katalog.data.artikel, { frisch: true })}>Alle neu aus Exxas</button>
          </div>
        )}
    </div>
  );
}
